import {
  confirmSignIn,
  fetchAuthSession,
  JWT,
  signIn,
  signOut,
  confirmResetPassword,
  getCurrentUser,
  fetchUserAttributes,
  type AuthUser,
  resetPassword,
} from 'aws-amplify/auth';
import api from '@services/api';
import { getContractorFromUEN } from '@services/contractors';
import { getTowncouncilFromUEN } from '@services/towncouncil';
import { getContractorUserInfo, getTCUserInfo } from '@services/users';
import { get } from '@services/utils';
import { TContractor } from '@typedefs/contractors';
import { TTowncouncil } from '@typedefs/towncouncils';
import { IContractorUser, ITCUser } from '@typedefs/users';
import { defineStore } from 'pinia';

type UserGroup = 'portal-tc' | 'portal-contractor' | 'dms-admin' | 'portal-tc-master' | 'portal-contractor-master';

interface IUserStore extends AuthUser {
  profileId: string;
  accessToken: string | null;
  idToken: string | null;
  pagePermissions: string[];
  userGroups: UserGroup[] | [];
  profile?: ITCUser | IContractorUser | undefined;
  company: TTowncouncil | TContractor | undefined;
  justLoggedIn: boolean;
  credentials?: {
    accessKeyId: string;
    secretAccessKey: string;
    sessionToken?: string;
    expiration?: Date;
  };
}

const emptyUser: IUserStore = {
  username: '',
  profileId: '',
  userId: '',
  signInDetails: {}, // Initialize with an empty object (can be adjusted based on actual structure)
  accessToken: null,
  idToken: null,
  userGroups: [],
  pagePermissions: [],
  profile: undefined,
  company: undefined,
  justLoggedIn: false,
};

export const useUserStore = defineStore('user', {
  state: (): IUserStore => emptyUser,
  //   const state = emptyUser;
  //   const auth = await fetchAuthSession();
  //   console.log(49, auth);
  //   return state;
  // },

  actions: {
    async login(username: string, password: string) {
      const { nextStep } = await signIn({
        username,
        password,
      });

      return nextStep;
    },
    async getCurrentUser() {
      try {
        const userDetails = await getCurrentUser();
        this.updateUser(userDetails);

        console.log(77);
        await this.successfullyLoggedin();
        return true;
      } catch (err) {
        console.log(err);
        return false;
      }
    },
    async confirmVerificationCode(username: string, password: string, code: string) {
      await confirmResetPassword({
        username,
        confirmationCode: code,
        newPassword: password,
      });
      return true;
    },
    async confirmFirstSignin(newPassword: string, userAttributes = {}) {
      const { nextStep } = await confirmSignIn({
        challengeResponse: newPassword,
        options: {
          userAttributes,
        },
      });
      return nextStep;
    },
    async confirmTOTP(otp: string) {
      const { nextStep } = await confirmSignIn({
        challengeResponse: otp,
      });

      return nextStep;
    },
    async successfullyLoggedin() {
      // @todo check on this for cyclic object value
      await this.fetchProfileId();
      await this.fetchTokens();
      await this.fetchPagePermissions();
      await this.fetchUserProfile();

      this.justLoggedIn = true;
      console.log('access token', this.accessToken);
      console.log('profile', this.profile);
      console.log('company', this.company);
      return true;
    },
    async logout() {
      await signOut();
      this.username = '';
      this.userId = '';
      this.profileId = '';
      this.signInDetails = {};
      this.accessToken = null;
      this.idToken = null;
      this.userGroups = [];
      this.company = undefined;
      this.pagePermissions = [];
    },
    async getCurrentAuthSession() {
      try {
        const { username, userId, signInDetails } = await getCurrentUser();
        const session = await fetchAuthSession();
        if (session) {
          const newInfo: any = {
            username: username,
            userId: userId,
            accessToken: session.tokens?.accessToken.toString(),
            idToken: session.tokens?.idToken?.toString(),
            credentials: session.credentials,
            signInDetails,
          };

          if (session.tokens?.accessToken && session.tokens?.accessToken.payload['cognito:groups']) {
            newInfo.userGroups = session.tokens.accessToken.payload['cognito:groups'] as UserGroup[];
          }
          this.updateUser(newInfo);
        }
      } catch (e) {
        console.error(e);
      }
    },
    updateUser(userInfo: Partial<Omit<IUserStore, 'pagePermissions'>>) {
      const { username, signInDetails, userId, profileId, userGroups, accessToken, idToken, credentials } = userInfo;
      if (typeof username !== 'undefined') this.username = username;
      if (typeof userId !== 'undefined') this.userId = userId;
      if (typeof signInDetails !== 'undefined') this.signInDetails = signInDetails;
      if (typeof profileId !== 'undefined') this.profileId = profileId!;
      if (typeof userGroups !== 'undefined') this.userGroups = userGroups;

      if (credentials) this.credentials = credentials;

      if (typeof accessToken !== 'undefined' && typeof idToken !== 'undefined' && accessToken && idToken) {
        this.accessToken = accessToken;
        this.idToken = idToken;
      }
    },
    updateTokens({ accessToken, idToken }: { accessToken: JWT; idToken: JWT }) {
      this.accessToken = accessToken.toString();
      this.idToken = idToken.toString();
    },
    async fetchProfileId() {
      console.log(167, 'fetching profile id');
      const attributes = await fetchUserAttributes();
      if (attributes['custom:profile_id']) {
        this.updateUser({ profileId: attributes['custom:profile_id'] });
      }
    },
    async fetchTokens() {
      console.log('fetching tokens');
      try {
        const session = await fetchAuthSession();

        // Access token, id token, and refresh token
        const accessToken = session.tokens?.accessToken;
        const idToken = session.tokens?.idToken;

        if (accessToken && accessToken.payload['cognito:groups']) {
          this.userGroups = accessToken.payload['cognito:groups'] as UserGroup[];
          console.log('populated user groups');
        }

        // Use the Pinia store to update the tokens
        if (typeof accessToken !== 'undefined' && typeof idToken !== 'undefined') {
          this.updateTokens({ accessToken, idToken });
        }
      } catch (err) {
        console.error('Error fetching session tokens', err);
        throw err;
      }
      console.log('fetched tokens');
    },
    async fetchUserProfile() {
      console.log('fetching user profile');
      if (this.profileId.length === 0) {
        throw new Error('No profile ID');
      } else if (!this.accessToken) {
        throw new Error('No access token');
      } else {
        if (this.profileId.startsWith('TCU')) {
          const { results } = await getTCUserInfo(this.profileId, this.accessToken);
          console.log(212, results);
          if (!!results.UEN_TC === false) {
            throw new Error('no profile found');
          }
          this.profile = { ...results };
          const { results: tcResults } = await getTowncouncilFromUEN(this.profile.UEN_TC, this.accessToken);
          if (tcResults.length === 1) {
            this.company = tcResults[0];
          } else {
            throw new Error('no company found');
          }
        } else if (this.profileId.startsWith('CTRU')) {
          const { results } = await getContractorUserInfo(this.profileId, this.accessToken);
          this.profile = { ...results };

          const { results: contractorResults } = await getContractorFromUEN(
            this.profile.UEN_Contractor,
            this.accessToken,
          );

          if (contractorResults.length === 1) {
            this.company = contractorResults[0];
          } else {
            throw new Error('no company found');
          }
        }
      }
    },
    async fetchPagePermissions() {
      console.log('fetch page permissions');
      console.log('existing page permissions', this.pagePermissions);
      if (this.pagePermissions.length === 0) {
        console.log('fetching permissions for these groups:', this.userGroups.join(', '));
        await Promise.allSettled(
          this.userGroups.map(async (groupId) => {
            if (this.accessToken) {
              try {
                const { results } = await get<{ results: string[] }>(api.facade.getPagePermissions(groupId), {
                  token: this.accessToken,
                });
                return results;
              } catch (e) {
                console.error(`Error fetching permissions for group ${groupId}:`, e);
                return []; // Return an empty array in case of error
              }
            } else {
              console.warn('No Access Token');
              return []; // Return empty array if no access token is available
            }
          }),
        ).then((results: PromiseSettledResult<string[]>[]) => {
          const prefix = 'portal:';

          // Flatten and filter out any invalid or empty permissions
          this.pagePermissions = results
            .filter((result) => result.status === 'fulfilled') // Only process successful promises
            .flatMap((result) => {
              const permissions = result.value;
              return permissions
                .map((permission) => {
                  if (permission.startsWith(prefix)) {
                    return permission.slice(prefix.length); // Remove prefix from permission
                  }
                  return null; // Exclude permission if it doesn't match the prefix
                })
                .filter((value) => value !== null); // Remove any null values (i.e., permissions that didn't match the prefix)
            });

          console.log('fetched page permissions');
        });
      }
      return true;
    },
    async requestPasswordReset(username: string) {
      const { nextStep } = await resetPassword({
        username,
      });

      return nextStep;
    },
  },

  getters: {
    isLoggedIn(state) {
      return !!state.username && !!state.accessToken && !!state.idToken && !!state.signInDetails;
    },
    hasProfile(state) {
      return (
        state.profileId.length > 0 && typeof state.profile !== 'undefined' && Object.keys(state.profile).length > 0
      );
    },
    hasCredentials(state) {
      return !!state.credentials;
    },
    isContractor(state) {
      return state.profileId.startsWith('CTRU');
    },
    isTowncouncil(state) {
      return state.profileId.startsWith('TCU');
    },
    isMasterUser(state) {
      return (
        typeof state.profile !== 'undefined' &&
        (state.profile.Role === 'Master Contractor' || state.profile.Role === 'Master Town Council')
      );
    },
  },
});
